import React from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
} from "@material-ui/core";
import styles from "./RecentPaymentsCard.module.css";

const currencyFmt = (v, code = "USD") =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: code,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(toNumber(v));

const toNumber = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const parsed = Number(String(value ?? "").replace(/[$,]/g, "").trim());
  return Number.isFinite(parsed) ? parsed : 0;
};

const toDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(+date) ? null : date;
};

const dateKey = (value) => {
  const date = toDate(value);
  if (!date) return "unknown";
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
};

const formatDate = (value) => {
  const date = toDate(value);
  if (!date) return "Unknown date";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatPaymentTime = (value) => {
  const date = toDate(value);
  if (!date) return "";
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
};

const customerNameFor = (record = {}) =>
  record.customerName ||
  record.clientStore ||
  record.clientName ||
  record.billToName ||
  record.billToStore ||
  record.shipToName ||
  record.shipToStore ||
  record.paidBy ||
  "Unknown customer";

const normalizePayments = (payments = [], maxItems) =>
  (Array.isArray(payments) ? payments : [])
    .slice(0, maxItems)
    .map((record, index) => ({
      ...record,
      amountPaid: toNumber(record?.amountPaid),
      customerName: customerNameFor(record),
      rowKey: String(record?._id || `${record?.invoiceId || "payment"}-${index}`),
    }))
    .sort((a, b) => (toDate(b.datePaid)?.getTime() || 0) - (toDate(a.datePaid)?.getTime() || 0));

const groupPayments = (payments = []) => {
  const dateMap = new Map();

  payments.forEach((payment) => {
    const key = dateKey(payment.datePaid);
    if (!dateMap.has(key)) {
      dateMap.set(key, {
        key,
        date: payment.datePaid,
        total: 0,
        count: 0,
        customerMap: new Map(),
      });
    }

    const dateGroup = dateMap.get(key);
    dateGroup.total += payment.amountPaid;
    dateGroup.count += 1;

    const customerKey = payment.customerName.toLowerCase();
    if (!dateGroup.customerMap.has(customerKey)) {
      dateGroup.customerMap.set(customerKey, {
        key: `${key}:${customerKey}`,
        name: payment.customerName,
        total: 0,
        count: 0,
        payments: [],
      });
    }

    const customerGroup = dateGroup.customerMap.get(customerKey);
    customerGroup.total += payment.amountPaid;
    customerGroup.count += 1;
    customerGroup.payments.push(payment);
  });

  return Array.from(dateMap.values())
    .map((dateGroup) => ({
      ...dateGroup,
      customers: Array.from(dateGroup.customerMap.values())
        .map((customer) => ({
          ...customer,
          payments: customer.payments.sort((a, b) => (toDate(b.datePaid)?.getTime() || 0) - (toDate(a.datePaid)?.getTime() || 0)),
        }))
        .sort((a, b) => b.total - a.total),
    }))
    .sort((a, b) => (toDate(b.date)?.getTime() || 0) - (toDate(a.date)?.getTime() || 0));
};

export default function RecentPaymentsCard({
  title = "Recent Payments",
  payments = [],
  currency = "USD",
  maxItems = 75,
}) {
  const normalizedPayments = React.useMemo(
    () => normalizePayments(payments, maxItems),
    [payments, maxItems]
  );
  const groupedPayments = React.useMemo(
    () => groupPayments(normalizedPayments),
    [normalizedPayments]
  );
  const totalAmount = React.useMemo(
    () => normalizedPayments.reduce((sum, payment) => sum + payment.amountPaid, 0),
    [normalizedPayments]
  );
  const [expandedDates, setExpandedDates] = React.useState({});
  const [expandedCustomers, setExpandedCustomers] = React.useState({});

  React.useEffect(() => {
    if (!groupedPayments.length) return;
    setExpandedDates((prev) => {
      if (Object.keys(prev).length) return prev;
      return { [groupedPayments[0].key]: true };
    });
  }, [groupedPayments]);

  const toggleDate = (key) => {
    setExpandedDates((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleCustomer = (key) => {
    setExpandedCustomers((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <Card variant="outlined" className={styles.card}>
      <CardHeader
        title={title}
        subheader={
          normalizedPayments.length
            ? `${normalizedPayments.length} payments • ${currencyFmt(totalAmount, currency)}`
            : "No payments logged"
        }
      />
      <CardContent className={styles.cardContent}>
        {groupedPayments.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            There are no recent payments to display.
          </Typography>
        ) : (
          <div className={styles.paymentGroups}>
            <div className={styles.summaryBar}>
              <span>Grouped by payment date</span>
              <strong>{currencyFmt(totalAmount, currency)}</strong>
            </div>
            {groupedPayments.map((dateGroup) => {
              const dateExpanded = Boolean(expandedDates[dateGroup.key]);
              return (
                <section key={dateGroup.key} className={styles.dateSection}>
                  <button
                    type="button"
                    className={styles.dateButton}
                    onClick={() => toggleDate(dateGroup.key)}
                    aria-expanded={dateExpanded}
                  >
                    <span className={`${styles.chevron} ${dateExpanded ? styles.chevronOpen : ""}`}>›</span>
                    <span className={styles.dateText}>
                      <strong>{formatDate(dateGroup.date)}</strong>
                      <span>
                        {dateGroup.customers.length} customer{dateGroup.customers.length === 1 ? "" : "s"} • {dateGroup.count} payment{dateGroup.count === 1 ? "" : "s"}
                      </span>
                    </span>
                    <strong className={styles.groupAmount}>{currencyFmt(dateGroup.total, currency)}</strong>
                  </button>

                  {dateExpanded && (
                    <div className={styles.customerList}>
                      {dateGroup.customers.map((customer) => {
                        const customerExpanded = Boolean(expandedCustomers[customer.key]);
                        return (
                          <div key={customer.key} className={styles.customerSection}>
                            <button
                              type="button"
                              className={styles.customerButton}
                              onClick={() => toggleCustomer(customer.key)}
                              aria-expanded={customerExpanded}
                            >
                              <span className={`${styles.chevron} ${customerExpanded ? styles.chevronOpen : ""}`}>›</span>
                              <span className={styles.customerText}>
                                <strong>{customer.name}</strong>
                                <span>
                                  {customer.count} payment{customer.count === 1 ? "" : "s"}
                                </span>
                              </span>
                              <strong className={styles.groupAmount}>{currencyFmt(customer.total, currency)}</strong>
                            </button>

                            {customerExpanded && (
                              <div className={styles.paymentList}>
                                <div className={styles.paymentHeader}>
                                  <span>Invoice</span>
                                  <span>Payment</span>
                                  <span>Amount</span>
                                </div>
                                {customer.payments.map((payment) => (
                                  <div key={payment.rowKey} className={styles.paymentRow}>
                                    <div className={styles.invoiceCell}>
                                      {payment.invoiceId ? (
                                        <Link className={styles.invoiceLink} to={`/invoice/${payment.invoiceId}`}>
                                          {payment.invoiceNumber ? `Invoice ${payment.invoiceNumber}` : "View invoice"}
                                        </Link>
                                      ) : (
                                        <span className={styles.invoiceLinkDisabled}>
                                          {payment.invoiceNumber ? `Invoice ${payment.invoiceNumber}` : "No invoice link"}
                                        </span>
                                      )}
                                      <span>{formatPaymentTime(payment.datePaid) || formatDate(payment.datePaid)}</span>
                                    </div>
                                    <div className={styles.paymentMeta}>
                                      <span>{payment.paymentMethod || "Payment"}</span>
                                      {payment.paidBy ? <span>{payment.paidBy}</span> : null}
                                      {payment.note ? <em>{payment.note}</em> : null}
                                    </div>
                                    <strong className={styles.paymentAmount}>
                                      {currencyFmt(payment.amountPaid, currency)}
                                    </strong>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
